import React, { useRef } from 'react';
import { FileText, Loader2, Sparkles, Upload } from 'lucide-react';
import { DocIntelMainCard } from './DocIntelUi';
import { DOC_INTEL_UPLOAD_ACCEPT } from './docIntelUtils';

const STEPS_DEAL = ['Upload file', 'AI extracts fields', 'You review & apply'];
const STEPS_TEMPLATE = ['Upload sample', 'AI detects fields', 'Build & promote template'];

const DocIntelUploadPanel = ({ onUploadFiles, isUploading, mode = 'deal' }) => {
  const isTemplateMode = mode === 'template';
  const steps = isTemplateMode ? STEPS_TEMPLATE : STEPS_DEAL;
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = React.useState(false);

  const handleFiles = (files) => {
    if (!files?.length || isUploading) return;
    onUploadFiles(Array.from(files));
    setDragOver(false);
  };

  return (
    <DocIntelMainCard>
      <div className="grid gap-0 lg:grid-cols-[1fr_240px]">
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => !isUploading && fileInputRef.current?.click()}
          className={`flex min-h-[320px] cursor-pointer flex-col items-center justify-center border-b border-slate-100 px-6 py-12 text-center transition lg:border-b-0 lg:border-r ${
            dragOver ? 'bg-teal-50/60' : 'bg-gradient-to-br from-teal-50/30 via-white to-slate-50/50'
          } ${isUploading ? 'pointer-events-none opacity-70' : ''}`}>
          <input
            ref={fileInputRef}
            type="file"
            className="sr-only"
            multiple
            accept={DOC_INTEL_UPLOAD_ACCEPT}
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-md ring-1 ring-teal-100">
            {isUploading ? (
              <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
            ) : (
              <Upload className="h-8 w-8 text-teal-800" strokeWidth={1.5} />
            )}
          </div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <Sparkles className="h-5 w-5 text-teal-700" />
            {isTemplateMode ? 'Upload a sample agreement' : 'Drop KYC & agreements here'}
          </h3>
          <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-600">
            {isTemplateMode
              ? 'Use your builder’s agreement PDF or DOCX. AI will detect merge fields for a reusable template.'
              : 'PDF, DOCX, JPG, PNG or plain text · up to 25 MB · multiple files supported'}
          </p>
          <span className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#2A565E] px-5 py-2.5 text-sm font-medium text-white shadow-sm">
            {isUploading ? 'Uploading…' : 'Browse files'}
          </span>
        </div>

        <div className="flex flex-col justify-center gap-4 px-5 py-8">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">How it works</p>
          <ol className="space-y-4">
            {steps.map((step, i) => (
              <li key={step} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-700/10 text-xs font-bold text-teal-800">
                  {i + 1}
                </span>
                <span className="pt-0.5 text-sm text-slate-700">{step}</span>
              </li>
            ))}
          </ol>
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50/80 p-3">
            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <p className="text-xs leading-relaxed text-slate-500">
              {isTemplateMode
                ? 'Templates are saved at project level and appear under Property Documents after promote.'
                : 'Separate from the attach list — smart uploads stay in this panel until applied to the deal.'}
            </p>
          </div>
        </div>
      </div>
    </DocIntelMainCard>
  );
};

export default DocIntelUploadPanel;
